import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { Checkbox } from "./field";
import { Skeleton } from "./feedback";
import { cn } from "./cn";

export type ColumnAlign = "left" | "center" | "right";

/**
 * Static lookups, not interpolation.
 *
 * The old queue table built its alignment class as `` `text-${align}` ``.
 * Tailwind's scanner cannot see a class assembled at runtime, so those utilities
 * were only in the bundle because `text-right` and `text-left` happened to appear
 * as literals elsewhere in the app — delete those and the table silently
 * left-aligns. Every dynamic class in this file resolves through a map of
 * literals for that reason.
 */
const ALIGN: Record<ColumnAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/**
 * Which columns survive a narrow screen.
 *
 * All five admin tables were desktop-only slabs that side-scrolled on a phone.
 * Hiding secondary columns beats duplicating the table into a parallel card list:
 * one DOM means one set of accessible names, one set of test targets, and no risk
 * of the two copies drifting.
 */
const PRIORITY: Record<"always" | "sm" | "md" | "lg" | "xl" | "2xl", string> = {
  always: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
  "2xl": "hidden 2xl:table-cell",
};

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: ColumnAlign;
  /** A Tailwind width class, e.g. `"w-48"`. */
  width?: string;
  /** Smallest breakpoint at which this column appears. Defaults to `always`. */
  priority?: keyof typeof PRIORITY;
  sortable?: boolean;
  /** Extra classes on the body cell. */
  cellClassName?: string;
  /**
   * Pin this column to the right edge, above the horizontal scroll.
   *
   * For the column holding a row's action. A wide table scrolls rather than
   * squeezes (see the scroll container below), and the rightmost column is the
   * first thing to go — which is fine for a secondary detail and not fine for
   * the only button on the row. Pinned, the action stays put and the columns
   * slide underneath it.
   *
   * The cell takes its background from the row (`bg-inherit`) so hover and
   * selection still read through, which is why `<tr>` carries an opaque base
   * colour rather than leaving it transparent.
   */
  pinned?: boolean;
}

export interface SortState {
  key: string;
  direction: "asc" | "desc";
}

/**
 * Optional banding of the body into sections.
 *
 * Groups are runs of *adjacent* rows sharing a key — deliberately not a
 * `Map`-and-regroup, which would silently reorder the table. The caller has
 * already decided the order (usually the server's `ORDER BY`), and grouping must
 * not be the thing that overrides it. Rows that share a key but arrive apart
 * therefore form two sections, which is the honest rendering of the data given:
 * it shows the caller their sort is not what they thought.
 */
export interface RowGroup<T> {
  /** Rows with equal keys, adjacent in `rows`, form one section. */
  key: (row: T) => string;
  /** The section heading, spanning the full width above its rows. */
  header: (rows: T[]) => ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Rendered in place of the body when there are no rows. */
  empty?: ReactNode;
  loading?: boolean;
  skeletonRows?: number;
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /** Band the body into sections. Omit for a flat table. */
  group?: RowGroup<T>;
  /** Minimum table width, e.g. `"min-w-[900px]"`, forcing scroll over squeeze. */
  minWidth?: string;
  stickyHeader?: boolean;
  selection?: {
    selected: ReadonlySet<string>;
    onToggle: (key: string) => void;
    onToggleAll: (keys: string[]) => void;
    /** Rows that cannot be selected — the checkbox renders disabled. */
    isSelectable?: (row: T) => boolean;
    /**
     * What the checkbox is called. Without it the label falls back to the row
     * key, and a screen reader reads out a UUID — "Select row
     * 3f8a91c2-…" names nothing the user can act on.
     */
    rowLabel?: (row: T) => string;
  };
  /**
   * Where a click on the row body goes — a **mouse convenience, never the only
   * way in**.
   *
   * A `<tr>` cannot take focus without claiming to be a control it is not, so
   * this handler is unreachable by keyboard by construction. That is fine only
   * because every caller that passes it also puts a **real button in a cell**
   * pointing at the same place, and that button is what the keyboard and the
   * accessibility tree use. Pass one without the other and the row's
   * destination becomes mouse-only.
   *
   * Cells holding their own controls stop the click from bubbling, so a
   * checkbox or a row action is never also a row click.
   */
  onRowClick?: (row: T) => void;
  caption?: string;
  className?: string;
  density?: keyof typeof PAD;
}

/**
 * Horizontal cell padding. `compact` is for a table with many columns beside the
 * sidebar, where 40px a column is the difference between fitting its card and
 * scrolling a column under the pinned action (the invoice inbox).
 */
const PAD: Record<"default" | "compact", string> = { default: "px-5", compact: "px-3" };
const HEAD_CELL_BASE = "py-3 font-medium text-micro uppercase tracking-wider text-subtle";
const BODY_CELL_BASE = "py-3 align-middle text-text";

/**
 * Rows split into runs of consecutive equal keys, in the order given.
 *
 * The React key is the group key plus its position, not the group key alone: a
 * key that legitimately recurs in two separate runs would otherwise collide and
 * React would reuse one section's DOM for the other.
 */
function groupRuns<T>(rows: T[], keyOf: (row: T) => string) {
  const runs: { key: string; rows: T[] }[] = [];
  let current: { key: string; rows: T[] } | null = null;
  let groupKey: string | null = null;
  for (const row of rows) {
    const key = keyOf(row);
    if (!current || key !== groupKey) {
      current = { key: `${key}-${runs.length}`, rows: [] };
      groupKey = key;
      runs.push(current);
    }
    current.rows.push(row);
  }
  return runs;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  empty,
  loading = false,
  skeletonRows = 6,
  sort,
  onSortChange,
  group,
  minWidth,
  stickyHeader = false,
  selection,
  onRowClick,
  caption,
  className = "",
  density = "default",
}: DataTableProps<T>) {
  const HEAD_CELL = cn(HEAD_CELL_BASE, PAD[density]);
  const BODY_CELL = cn(BODY_CELL_BASE, PAD[density]);
  const colSpan = columns.length + (selection ? 1 : 0);

  const selectableRows = selection
    ? rows.filter((row) => selection.isSelectable?.(row) ?? true)
    : [];
  const selectableKeys = selectableRows.map(getRowKey);
  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selection!.selected.has(key));
  const someSelected =
    !allSelected && selectableKeys.some((key) => selection!.selected.has(key));

  function toggleSort(key: string) {
    if (!onSortChange) return;
    onSortChange({
      key,
      direction: sort?.key === key && sort.direction === "asc" ? "desc" : "asc",
    });
  }

  function renderRow(row: T) {
    const key = getRowKey(row);
    const selectable = selection?.isSelectable?.(row) ?? true;
    // `onRowClick` is a mouse affordance only — a row is not a control and
    // cannot be focused without lying about what it is. It duplicates a real
    // button in one of the cells rather than replacing it; see the prop's own
    // note for the contract that keeps this honest.
    return (
      <tr
        key={key}
        onClick={
          onRowClick
            ? (event) => {
                // React bubbles through the component tree, not the DOM, so a
                // dialog portalled out of a cell would otherwise open the row
                // on every click inside it. Only clicks that land in the row count.
                if (event.currentTarget.contains(event.target as Node)) onRowClick(row);
              }
            : undefined
        }
        className={cn(
          // Opaque, not transparent: a pinned cell inherits this, and a
          // see-through one would let the scrolled columns run under the action.
          "group/row bg-surface transition-colors",
          onRowClick && "cursor-pointer",
          selection?.selected.has(key)
            ? "bg-accent-soft/40"
            : "hover:bg-surface-hover"
        )}
      >
        {selection && (
          // Selecting is not opening: the checkbox owns its click outright.
          <td
            className={cn(BODY_CELL, "pr-0")}
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              aria-label={`Select ${selection.rowLabel?.(row) ?? `row ${key}`}`}
              checked={selection.selected.has(key)}
              disabled={!selectable}
              onChange={() => selection.onToggle(key)}
            />
          </td>
        )}
        {columns.map((column) => (
          <td
            key={column.key}
            className={cn(
              column.pinned && "sticky right-0 z-[1] bg-inherit shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]",
              BODY_CELL,
              ALIGN[column.align ?? "left"],
              PRIORITY[column.priority ?? "always"],
              column.cellClassName
            )}
          >
            {column.cell(row)}
          </td>
        ))}
      </tr>
    );
  }

  return (
    /*
     * `overflow-x-auto` makes this element a scroll container, and CSS forces
     * `overflow-y` to `auto` alongside it — so a `sticky` thead inside resolves
     * against *this* box, not the viewport. Offsetting it by the app header's
     * height (`top-16`) therefore pushed the header 64px down inside the table
     * and parked it on top of the first row.
     *
     * So the scroll region is made explicit instead: when `stickyHeader` is on,
     * the table body scrolls within a bounded height and the header sticks to
     * the top of that box. That works at any page scroll position, which the
     * viewport-relative version never actually did.
     *
     * `relative`, so an `sr-only` header label — `position: absolute` — is
     * placed inside this box. With no positioned ancestor it resolved against
     * the page, at the table's far edge, escaped the scroll and widened the
     * whole page on a phone.
     */
    <div
      className={cn(
        "relative overflow-x-auto scrollbar-thin",
        stickyHeader && "max-h-[70vh] overflow-y-auto",
        className
      )}
    >
      <table className={cn("w-full text-sm", minWidth)}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead
          className={cn(
            "bg-surface-sunken",
            stickyHeader && "sticky top-0 z-[5]"
          )}
        >
          <tr className="border-b border-line">
            {selection && (
              <th scope="col" className={cn(HEAD_CELL, "w-10 pr-0")}>
                <Checkbox
                  aria-label="Select all rows"
                  checked={allSelected}
                  ref={(node) => {
                    if (node) node.indeterminate = someSelected;
                  }}
                  disabled={selectableKeys.length === 0}
                  onChange={() => selection.onToggleAll(selectableKeys)}
                />
              </th>
            )}
            {columns.map((column) => {
              const align = ALIGN[column.align ?? "left"];
              const priority = PRIORITY[column.priority ?? "always"];
              // Narrowed to the state object rather than a boolean, so
              // `activeSort.direction` needs no non-null assertion below.
              const activeSort = sort && sort.key === column.key ? sort : null;
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    HEAD_CELL,
                    column.pinned && "sticky right-0 z-[1] bg-surface-sunken",
                    align,
                    priority,
                    column.width
                  )}
                  aria-sort={
                    activeSort
                      ? activeSort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : column.sortable
                        ? "none"
                        : undefined
                  }
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className={cn(
                        // `uppercase` again: preflight resets a button's
                        // text-transform, so sortable headers read in a
                        // different case from their plain neighbours.
                        // `align-top`: an inline-flex box's baseline comes from its
                        // first item, which `flex-row-reverse` makes the icon — so a
                        // right-aligned header sat a pixel off its neighbours.
                        "inline-flex cursor-pointer items-center gap-1 rounded-sm align-top uppercase tracking-wider transition-colors hover:text-text",
                        activeSort && "text-text",
                        column.align === "right" && "flex-row-reverse"
                      )}
                    >
                      {column.header}
                      {activeSort ? (
                        activeSort.direction === "asc" ? (
                          <ArrowUp className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="h-3 w-3" aria-hidden="true" />
                        )
                      ) : (
                        <ChevronsUpDown
                          className="h-3 w-3 opacity-40"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody className="divide-y divide-line">
          {loading ? (
            Array.from({ length: skeletonRows }, (_, index) => (
              <tr key={`skeleton-${index}`}>
                {selection && (
                  <td className={cn(BODY_CELL, "pr-0")}>
                    <Skeleton className="h-4 w-4" />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(BODY_CELL, PRIORITY[column.priority ?? "always"])}
                  >
                    <Skeleton className="h-3.5 w-full max-w-[10rem]" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="p-0">
                {empty}
              </td>
            </tr>
          ) : group ? (
            groupRuns(rows, group.key).map(({ key, rows: groupRows }) => (
              // A fragment, not a <tbody> per group: several tbodies would each
              // start their own `divide-y`, so the seam between two sections
              // would be the one place the table has no rule.
              <Fragment key={key}>
                <tr className="bg-surface-sunken">
                  {/*
                   * `colgroup`, not `row`: the heading describes the columns
                   * below it, not the cells beside it (there are none). This is
                   * what lets a screen reader announce the section a row belongs
                   * to — the whole point of dropping the repeated cell.
                   */}
                  <th
                    scope="colgroup"
                    colSpan={colSpan}
                    className="px-5 py-2 text-left text-xs font-semibold text-text"
                  >
                    {group.header(groupRows)}
                  </th>
                </tr>
                {groupRows.map(renderRow)}
              </Fragment>
            ))
          ) : (
            rows.map(renderRow)
          )}
        </tbody>
      </table>
    </div>
  );
}
